import { AssetContentFormat } from "../api/model/snapshots/Asset";
import * as assetExports from "../electron/assetExports";
import { AssetContent, DownloadableAsset } from "../model/Asset";
import AssetDownloadTarget from "../model/AssetDownloadTarget";
import NamingConvention from "../model/NamingConvention";
import electron from "../electron";
import { DENSITY, PLATFORM } from "../enums";

import { getExportName, isAssetDensityScaleSelected, isSupportedAssetContent } from "./asset";
import BasicRecord from "./BasicRecord";
import { getCalendarDateOfISOString } from "./date";
import { triggerDownload } from "./download";
import { MIME_TYPE } from "./file";
import { createNewZip } from "./jszip";
import { getDensityScale, getDensityScaleRepresentation } from "./legacy";
import { exists } from "./object";
import { canDownload } from "./platform";

interface AssetVariant extends AssetContent {
    localName: string;
    layerName: string;
    displayName: string;
}

interface AssetVariantWithData extends AssetVariant {
    data: ArrayBuffer;
}

const ASSETS_DEFAULT_DOWNLOAD_NAME = "assets.zip";

function getFileName(assetData: AssetVariantWithData, namingConvention: NamingConvention, platform: PLATFORM) {
    const fileName = getExportName(assetData, namingConvention, platform);

    // Vectoral formats
    if ([AssetContentFormat.Svg, AssetContentFormat.Pdf].includes(assetData.format)) {
        return fileName;
    }

    // Bitmap formats
    const { density, densityScale = getDensityScale(density) } = assetData;
    let computedFileName = fileName;

    if (assetData.optimized && assetData.optimized.status !== "ready") {
        computedFileName += " (non-optimized)";
    }

    if (densityScale !== DENSITY["BASE"]) {
        computedFileName += `@${densityScale}x`;
    }

    return computedFileName;
}

function findAssetVariantsOfFormat(
    asset: DownloadableAsset,
    assetFormat: string,
    platform: PLATFORM
): AssetVariant[] {
    const {
        localName,
        layerName,
        displayName,
        contents
    } = asset;

    return contents
        .filter(content => content.format === assetFormat && isSupportedAssetContent(platform, content))
        .map(assetVariant => ({
            localName: localName!,
            layerName,
            displayName,
            ...assetVariant
        }));
}

type GetExportableDataArgs = {
    variant: AssetVariant;
    showOptimizedSVG: boolean;
    showOptimizedPNG: boolean;
    showOptimizedJPG: boolean;
};

function getExportableData({
    variant,
    showOptimizedSVG,
    showOptimizedPNG,
    showOptimizedJPG
}: GetExportableDataArgs): Promise<AssetVariantWithData> | null {
    const isSVGOptimizationAvailable = showOptimizedSVG && variant.format === AssetContentFormat.Svg;
    const isPNGOptimizationAvailable = showOptimizedPNG && variant.format === AssetContentFormat.Png;
    const isJPGOptimizationAvailable = showOptimizedJPG && variant.format === AssetContentFormat.Jpg;
    const isOptimizedVariantReady = variant.optimized && variant.optimized.status === "ready";

    if (
        (variant.format === AssetContentFormat.Webp || variant.format === AssetContentFormat.Avif) &&
        !isOptimizedVariantReady
    ) {
        return null;
    }

    if (isSVGOptimizationAvailable) {
        Object.assign(variant, variant.optimized);
    } else if (
        (
            isPNGOptimizationAvailable ||
            isJPGOptimizationAvailable ||
            variant.format === AssetContentFormat.Webp ||
            variant.format === AssetContentFormat.Avif
        ) && isOptimizedVariantReady
    ) {
        variant.url = variant.optimized!.url!;
    }

    return fetch(variant.url)
        .then(response => response.arrayBuffer())
        .then(data => ({ data, ...variant }));
}

type GetAssetDataOfFormatArgs = {
    filteredAssets: DownloadableAsset[];
    platform: PLATFORM;
    format: AssetContentFormat;
    showOptimizedSVG: boolean;
    showOptimizedPNG: boolean;
    showOptimizedJPG: boolean;
};

function getAssetDataOfFormat({
    filteredAssets,
    platform,
    format,
    showOptimizedSVG,
    showOptimizedPNG,
    showOptimizedJPG
}: GetAssetDataOfFormatArgs): Promise<AssetVariantWithData[]> {
    const variants = filteredAssets.flatMap(asset =>
        findAssetVariantsOfFormat(asset, format, platform)
    );

    return Promise.all(
        variants.map(variant => getExportableData({
            variant,
            showOptimizedSVG,
            showOptimizedPNG,
            showOptimizedJPG
        })).filter(exists)
    );
}

async function prepareAssetPackage(
    assetsWithData: AssetVariantWithData[],
    namingFunction = (asset: AssetVariantWithData) => asset.displayName
) {
    const nameCounter: BasicRecord<number> = {};
    const assetZip = await createNewZip();

    assetsWithData.forEach(asset => {
        let processedAsset = asset;
        const assetKey = namingFunction(processedAsset) + processedAsset.format;

        if (!(assetKey in nameCounter)) {
            nameCounter[assetKey] = 0;
        }

        const count = ++nameCounter[assetKey];

        if (count > 1) {
            processedAsset = { ...asset };
            processedAsset.displayName += `_${count}`;
            processedAsset.layerName += `_${count}`;
        }

        assetZip.file(`${namingFunction(processedAsset)}.${processedAsset.format}`, processedAsset.data);
    });

    return assetZip;
}

async function prepareAndroidAssets(
    containerName: string,
    assetsWithData: AssetVariantWithData[],
    multiple = false
) {
    const zip = await prepareAssetPackage(assetsWithData, asset => {
        const name = getExportName(asset, NamingConvention.SnakeCase, PLATFORM.ANDROID);
        const { density, densityScale = getDensityScale(density) } = asset;
        const densityBucket = getDensityScaleRepresentation(PLATFORM.ANDROID, densityScale);

        return asset.format === AssetContentFormat.Svg ? name : `drawable-${densityBucket}/${name}`;
    });

    const blob = await zip.generateAsync({ type: "blob" });
    const fileName = multiple ? `assets_${containerName}` : getExportName(assetsWithData[0], NamingConvention.SnakeCase, PLATFORM.ANDROID);

    return {
        data: URL.createObjectURL(blob),
        name: `${fileName}_${getCalendarDateOfISOString()}.zip`
    };
}

// eslint-disable-next-line max-params
async function prepareAssets(
    containerName: string,
    assetsWithData: AssetVariantWithData[],
    assetNamingConvention: NamingConvention,
    platform: PLATFORM,
    multiple = false
) {
    if (assetsWithData.length === 1) {
        const [assetData] = assetsWithData;

        const blob = new Blob([assetData.data], { type: MIME_TYPE[assetData.format] });
        const fileName = `${getFileName(assetData, assetNamingConvention, platform)}.${assetData.format}`;

        return Promise.resolve({
            data: URL.createObjectURL(blob),
            name: fileName
        });
    }

    const assetZip = await prepareAssetPackage(
        assetsWithData,
        asset => getFileName(asset, assetNamingConvention, platform)
    );

    const blob = await assetZip.generateAsync({ type: "blob" });
    const fileName = multiple ? `assets_${containerName}` : getExportName(assetsWithData[0], assetNamingConvention, platform);

    return {
        data: URL.createObjectURL(blob),
        name: `${fileName}_${getCalendarDateOfISOString()}.zip`
    };
}

type TransformAssetForElectronAppArgs = {
    asset: DownloadableAsset;
    platform: PLATFORM;
    format: AssetContentFormat;
    showOptimizedSVG: boolean;
    showOptimizedPNG: boolean;
    showOptimizedJPG: boolean;
};

function transformAssetForElectronApp({
    asset,
    platform,
    format,
    showOptimizedSVG,
    showOptimizedPNG,
    showOptimizedJPG
}: TransformAssetForElectronAppArgs): DownloadableAsset {
    return {
        ...asset,
        contents: asset.contents.filter(content => isSupportedAssetContent(platform, content)).map(variant => {
            // electron app doesn't know about show optimized assets and
            // therefore doesn't look for variant.optimized when downloading assets
            // it only downloads variant.url
            // therefore, to be able to fix this issue in a backward compatible way
            // we're transforming the data here and
            // putting variant.optimized.url into variant.url
            const isSVGOptimizationAvailable = showOptimizedSVG && variant.format === AssetContentFormat.Svg &&
                format === AssetContentFormat.Svg;
            const isPNGOptimizationAvailable = showOptimizedPNG && variant.format === AssetContentFormat.Png &&
                format === AssetContentFormat.Png;
            const isJPGOptimizationAvailable = showOptimizedJPG && variant.format === AssetContentFormat.Jpg &&
                format === AssetContentFormat.Jpg;
            const isWEBPOptimizationAvailable = variant.format === AssetContentFormat.Webp &&
                format === AssetContentFormat.Webp;
            const isAvifOptimizationAvailable = variant.format === AssetContentFormat.Avif &&
                format === AssetContentFormat.Avif;
            let { url } = variant;

            if ((isWEBPOptimizationAvailable || isAvifOptimizationAvailable) && (!variant.optimized || variant.optimized.status !== "ready")) {
                return null;
            }

            if (variant.optimized && (
                isSVGOptimizationAvailable ||
                ((isPNGOptimizationAvailable || isJPGOptimizationAvailable || isWEBPOptimizationAvailable || isAvifOptimizationAvailable) && variant.optimized.status === "ready")
            )) {
                url = variant.optimized.url!;
            }

            // old windows apps looks for density in asset contents to calculate folder paths
            const { densityScale, density = getDensityScaleRepresentation(platform, densityScale) } = variant;
            return {
                ...variant,
                url,
                density
            };
        }).filter(exists)
    };
}

type TransformAssetsForElectronAppArgs = {
    assetsToSend: DownloadableAsset[];
    platform: PLATFORM;
    format: AssetContentFormat;
    showOptimizedSVG: boolean;
    showOptimizedPNG: boolean;
    showOptimizedJPG: boolean;
};

function transformAssetsForElectronApp({
    assetsToSend,
    platform,
    format,
    showOptimizedSVG,
    showOptimizedPNG,
    showOptimizedJPG
}: TransformAssetsForElectronAppArgs) {
    return assetsToSend.map(asset => transformAssetForElectronApp({
        asset,
        platform,
        format,
        showOptimizedSVG,
        showOptimizedPNG,
        showOptimizedJPG
    }));
}

type AssetDensityScalePrefs = BasicRecord<number[]>;

function getFilteredAssets(
    assets: DownloadableAsset[],
    assetDensityScalePrefs: AssetDensityScalePrefs
): DownloadableAsset[] {
    return assets.map(asset => ({
        ...asset,
        contents: asset.contents.filter(content => {
            const { format, density, densityScale = getDensityScale(density) } = content;
            return isAssetDensityScaleSelected(assetDensityScalePrefs, format, densityScale);
        })
    }));
}

type DownloadAssetsArgs = {
    barrelId: string;
    platform: PLATFORM;
    containerName: string;
    assets: DownloadableAsset[];
    selectedAssets: string[] | "all";
    format: AssetContentFormat;
    assetDensityScalePrefs: AssetDensityScalePrefs;
    assetNamingConvention: NamingConvention;
    showOptimizedSVG: boolean;
    showOptimizedPNG: boolean;
    showOptimizedJPG: boolean;
    toastContainer: HTMLElement;
    downloadTarget?: AssetDownloadTarget;
    shouldBrowseNewFolder?: boolean;
};

function downloadAssets({
    platform,
    containerName,
    assets,
    selectedAssets,
    format,
    assetDensityScalePrefs,
    assetNamingConvention,
    showOptimizedSVG,
    showOptimizedPNG,
    showOptimizedJPG,
    toastContainer,
    downloadTarget,
    shouldBrowseNewFolder
}: DownloadAssetsArgs) {
    if (!canDownload()) {
        return;
    }

    const assetsToDownload = selectedAssets === "all"
        ? assets
        : assets.filter(asset => selectedAssets.includes(asset._id));

    const filteredAssets = getFilteredAssets(assetsToDownload, assetDensityScalePrefs);

    if (electron) {
        const transformedAssets = transformAssetsForElectronApp({
            assetsToSend: filteredAssets,
            platform,
            format,
            showOptimizedSVG,
            showOptimizedPNG,
            showOptimizedJPG
        });

        assetExports.downloadAssets({
            platform,
            format,
            assets: transformedAssets,
            namingConvention: assetNamingConvention,
            toastContainer,
            downloadTarget,
            shouldBrowseNewFolder
        });

        return;
    }

    const multiple = filteredAssets.length > 1;

    getAssetDataOfFormat({
        filteredAssets,
        platform,
        format,
        showOptimizedSVG,
        showOptimizedPNG,
        showOptimizedJPG
    }).then(assetsWithData => {
        if (platform === PLATFORM.ANDROID) {
            return prepareAndroidAssets(containerName, assetsWithData, multiple);
        }

        return prepareAssets(containerName, assetsWithData, assetNamingConvention, platform, multiple);
    }).then(file => triggerDownload(file, ASSETS_DEFAULT_DOWNLOAD_NAME));
}

export {
    downloadAssets
};
